// Custom funnel chart component for transfers funnel visualization
// Creates a visual funnel representation with step-by-step breakdown

import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  useColorModeValue,
  Flex,
  Badge,
  Tooltip,
} from '@chakra-ui/react';

const CustomFunnelChart = ({ data, onStepClick }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const arrowColor = useColorModeValue('gray.300', 'gray.600');
  const summaryBg = useColorModeValue('blue.50', 'blue.900');
  const summaryBorder = useColorModeValue('blue.200', 'blue.700');
  const summaryText = useColorModeValue('blue.800', 'blue.200');
  const summaryConversion = useColorModeValue('blue.600', 'blue.300');

  const formatNumber = (num) => {
    return new Intl.NumberFormat('he-IL').format(num);
  };

  const formatPercentage = (num) => {
    return `${num.toFixed(1)}%`;
  };

  const getStepColor = (index) => {
    const colors = ['blue', 'green', 'orange', 'red'];
    return colors[index] || 'gray';
  };

  const getStepWidth = (value, maxValue) => {
    return Math.max((value / maxValue) * 100, 10); // Minimum 10% width
  };

  const getParameterDisplayName = (value) => {
    const displayNames = {
      'home_view': 'מסך בית',
      'tracking_view': 'מעקב',
      'shift_details_view': 'משמרת',
      'custom': 'מותאם',
      'fixed': 'קבוע'
    };
    return displayNames[value] || value;
  };

  const maxValue = Math.max(...data.map(step => step.value));

  return (
    <VStack spacing={6} align="stretch" p={4} dir="rtl">
      {data.map((step, index) => {
        const stepWidth = getStepWidth(step.value, maxValue);
        const colorScheme = getStepColor(index);
        
        return (
          <Box key={step.stepNumber} position="relative">
            {/* Step Container */}
            <Box
              bg={bgColor}
              border="2px"
              borderColor={`${colorScheme}.200`}
              borderRadius="lg"
              p={4}
              cursor={onStepClick ? 'pointer' : 'default'}
              transition="all 0.2s"
              _hover={onStepClick ? {
                borderColor: `${colorScheme}.400`,
                transform: 'translateY(-2px)',
                boxShadow: 'lg'
              } : {}}
              onClick={() => onStepClick && onStepClick(step)}
            >
              <VStack spacing={3} align="stretch">
                {/* Step Header */}
                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="2xl" fontWeight="bold" color={`${colorScheme}.500`}>
                      {formatNumber(step.value)}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      אירועים
                    </Text>
                  </VStack>
                  <HStack spacing={3} align="center">
                    <Text fontSize="lg" fontWeight="bold" color={textColor} textAlign="right">
                      {step.name}
                    </Text>
                    <Badge colorScheme={colorScheme} fontSize="sm" px={3} py={1}>
                      שלב {step.stepNumber}
                    </Badge>
                  </HStack>
                </HStack>

                {/* Funnel Visual Representation */}
                <Box position="relative" height="60px">
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    width={`${stepWidth}%`}
                    height="40px"
                    bg={`${colorScheme}.100`}
                    border="2px"
                    borderColor={`${colorScheme}.300`}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s"
                  >
                    <Text fontSize="sm" fontWeight="semibold" color={`${colorScheme}.700`}>
                      {formatPercentage((step.value / maxValue) * 100)}
                    </Text>
                  </Box>
                </Box>

                {/* Conversion Metrics */}
                {index > 0 && (
                  <HStack justify="space-around" pt={2}>
                    <Tooltip label="שיעור המרה מהשלב הקודם">
                      <VStack spacing={1}>
                        <Text fontSize="xs" color="gray.500">
                          המרה
                        </Text>
                        <Badge colorScheme="green" variant="subtle">
                          {formatPercentage(step.conversionRate)}
                        </Badge>
                      </VStack>
                    </Tooltip>
                    <Tooltip label="שיעור נשירה מהשלב הקודם">
                      <VStack spacing={1}>
                        <Text fontSize="xs" color="gray.500">
                          נשירה
                        </Text>
                        <Badge colorScheme="red" variant="subtle">
                          {formatPercentage(step.dropOffRate)}
                        </Badge>
                      </VStack>
                    </Tooltip>
                  </HStack>
                )}

                {/* Parameter Breakdown Preview */}
                {step.parameterBreakdown && Object.keys(step.parameterBreakdown).length > 0 && (
                  <Box pt={4} borderTop="1px" borderColor="gray.200">
                    <VStack spacing={3}>
                      <Text fontSize="sm" color="gray.600" fontWeight="bold" textAlign="center">
                        פירוט פרמטרים
                      </Text>
                      {Object.entries(step.parameterBreakdown).map(([paramName, paramValues]) => (
                        <Box key={paramName} w="100%">
                          <Text fontSize="xs" color="gray.700" mb={2} fontWeight="semibold" textAlign="right">
                            {paramName === 'from' ? 'מקור:' :
                             paramName === 'origin' ? 'סוג:' : `${paramName}:`}
                          </Text>
                          <HStack spacing={2} wrap="wrap" justify="flex-end">
                            {Object.entries(paramValues).map(([value, count]) => (
                              <Badge
                                key={value}
                                size="sm"
                                colorScheme={colorScheme}
                                variant="solid"
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                              >
                                {getParameterDisplayName(value)}: {formatNumber(count)}
                              </Badge>
                            ))}
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Arrow to next step */}
            {index < data.length - 1 && (
              <Flex justify="center" my={2}>
                <Box
                  width="0"
                  height="0"
                  borderLeft="15px solid transparent"
                  borderRight="15px solid transparent"
                  borderTop={`20px solid ${arrowColor}`}
                />
              </Flex>
            )}
          </Box>
        );
      })}

      {/* Overall Conversion Summary */}
      <Box
        mt={6}
        p={4}
        bg={summaryBg}
        border="1px"
        borderColor={summaryBorder}
        borderRadius="lg"
      >
        <VStack spacing={2}>
          <Text fontSize="lg" fontWeight="bold" color={summaryText}>
            סיכום כולל
          </Text>
          <HStack spacing={6}>
            <VStack spacing={1}>
              <Text fontSize="sm" color="gray.600">
                התחלות
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                {formatNumber(data[0]?.value || 0)}
              </Text>
            </VStack>
            <Box width="50px">
              <Progress
                value={((data[data.length - 1]?.value || 0) / (data[0]?.value || 1)) * 100}
                colorScheme="blue"
                size="lg"
                borderRadius="full"
              />
            </Box>
            <VStack spacing={1}>
              <Text fontSize="sm" color="gray.600">
                השלמות
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                {formatNumber(data[data.length - 1]?.value || 0)}
              </Text>
            </VStack>
          </HStack>
          <Text fontSize="lg" fontWeight="bold" color={summaryConversion}>
            שיעור המרה כולל: {formatPercentage(((data[data.length - 1]?.value || 0) / (data[0]?.value || 1)) * 100)}
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
};

export default CustomFunnelChart;
